
import React, { useState, useEffect } from 'react';
import { ShoppingCart, PackageCheck } from 'lucide-react';
import { User, SiteTheme } from '../../types';
import { useNavigate } from 'react-router-dom';
import { BrandLogo } from '../ui/BrandLogo';

interface StoreHeaderProps {
  student?: User;
  className?: string; // New: Class name to display
  cartCount: number;
  onOpenCart: () => void;
  theme?: SiteTheme;
  isPreview?: boolean;
  scrollContainerId?: string; 
}

export const StoreHeader: React.FC<StoreHeaderProps> = ({ student, className, cartCount, onOpenCart, theme, isPreview = false, scrollContainerId }) => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
      const target = scrollContainerId ? document.getElementById(scrollContainerId) : window;
      
      const handleScroll = () => {
          const y = scrollContainerId && target instanceof HTMLElement 
              ? target.scrollTop 
              : window.scrollY;
          
          const shouldBeScrolled = y > 20;
          
          setIsScrolled(prev => {
              if (prev !== shouldBeScrolled) return shouldBeScrolled;
              return prev;
          });
      };

      target?.addEventListener('scroll', handleScroll, { passive: true });
      handleScroll(); 
      
      return () => target?.removeEventListener('scroll', handleScroll);
  }, [scrollContainerId]);

  return (
    <header className={`${isPreview ? 'sticky' : 'fixed'} top-0 left-0 right-0 z-40 transition-all duration-500`}>
        {/* Background Layer */}
        <div 
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${isScrolled ? 'opacity-100' : 'opacity-0'}`}
            style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.92)', 
                backdropFilter: 'blur(12px)',
                borderBottom: '1px solid rgba(0,0,0,0.05)'
            }}
        />
        
        <div 
            className={`absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/30 via-black/5 to-transparent transition-opacity duration-500 pointer-events-none ${isScrolled ? 'opacity-0' : 'opacity-100'}`}
        />

        {/* Content Container */}
        <div className="relative max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex flex-col cursor-pointer group" onClick={() => !isPreview && navigate('/')}>
             {/* Logo Wrapper */}
             <div className="relative h-8 w-40 flex items-center">
                 <div className={`absolute left-0 top-1/2 -translate-y-1/2 transition-all duration-500 ${isScrolled ? 'opacity-0 scale-95 -translate-x-2' : 'opacity-100 scale-100 translate-x-0'}`}>
                    <div className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]">
                        <BrandLogo variant="light" layout="minimal" theme={theme} />
                    </div>
                 </div>
                 <div className={`absolute left-0 top-1/2 -translate-y-1/2 transition-all duration-500 ${isScrolled ? 'opacity-100 scale-90 translate-x-0' : 'opacity-0 scale-95 translate-x-2'}`}>
                    <BrandLogo variant="dark" layout="minimal" theme={theme} />
                 </div>
             </div>

          </div>
          
          <div className="flex items-center gap-2">
              <button 
                onClick={() => !isPreview && navigate('/my-orders')}
                className={`hidden md:flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-500 font-bold text-[10px] uppercase tracking-widest ${
                    isScrolled 
                    ? 'text-stone-600 hover:text-coffee-900 hover:bg-stone-100' 
                    : 'text-white hover:bg-white/20 bg-black/10 backdrop-blur-md drop-shadow-sm border border-white/10'
                }`}
              >
                  <PackageCheck size={14} /> Spåra Order
              </button>

              <button 
                onClick={onOpenCart} 
                className={`relative flex items-center gap-3 px-5 py-2.5 rounded-full transition-all duration-500 group ${
                    isScrolled 
                    ? 'bg-coffee-900 text-white shadow-md hover:bg-coffee-800' 
                    : 'bg-black/20 backdrop-blur-md border border-white/20 text-white hover:bg-black/30 shadow-lg'
                }`}
              >
                <ShoppingCart size={18} className="transition-transform group-hover:scale-110" />
                <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">Varukorg</span>
                {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center shadow-md border-2 border-white animate-fade-in">
                        {cartCount}
                    </span>
                )}
              </button>
          </div>
        </div>
    </header>
  );
};
